import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { getDashboardSummary, myOrders, myNotifications, markNotificationRead, cancelSubscription, mySubmissions } from '../services/dashboardService';
import { listMyRecordings } from '../services/recordingsService';
import {
  Home, MessageCircle, Mic, GraduationCap, TrendingUp, MessageSquare,
  Sparkles, Gift, CreditCard, Bell, Settings, Flame, Clock, BookOpen,
  Play, Send, Download, ChevronRight, Calendar, Award, CheckCircle2,
  Lock, ArrowUpRight, Volume2, Paperclip, Search, Filter, Globe,
} from 'lucide-react';

/* ===========================================================
   MOCKED DATA — realistic shapes for future backend wiring
=========================================================== */
const MOCK_PROFILE = {
  level: 'A2',
  streakDays: 18,
  speakingMinutes: 27 * 60 + 15,
  activitiesCompleted: 142,
  joinedAt: '2025-09-12',
};

const MOCK_CONTINUE = [
  { id: 'cont-1', module: 'Jeux de rôle A2', activity: 'Réservation d\'hôtel perdue', href: '/speaking-lab/jeux-de-role', progress: 60 },
  { id: 'cont-2', module: 'Compréhension orale B1', activity: 'Reportage économique', href: '/speaking-lab', progress: 30 },
  { id: 'cont-3', module: 'Expression orale guidée', activity: 'Décris ta journée idéale', href: '/speaking-lab/expression-orale-guidee', progress: 80 },
];

const MOCK_THREADS = [
  {
    id: 't1', topic: '🎤 A2 — Réservation d\'hôtel', updatedAt: '2026-02-18T09:24:00Z', unread: 2,
    counterpart: { name: 'Mme Laurent', role: 'Professeure' },
    messages: [
      { id: 'm1', from: 'student', kind: 'audio', preview: 'Enregistrement · 1 min 12 sec', at: '2026-02-17T14:02:00Z' },
      { id: 'm2', from: 'ai', kind: 'analysis', preview: 'Prononciation 84%, Fluidité 79%, Vocabulaire 81%', at: '2026-02-17T14:02:30Z' },
      { id: 'm3', from: 'teacher', kind: 'audio', preview: 'Retour audio · 47 sec', at: '2026-02-18T09:21:00Z' },
      { id: 'm4', from: 'teacher', kind: 'text', preview: 'Bel effort ! Travaille la liaison « hôtel-perdu » et reformule plus calmement.', at: '2026-02-18T09:24:00Z' },
    ],
  },
  {
    id: 't2', topic: '🎤 A2 — Au restaurant', updatedAt: '2026-02-15T17:40:00Z', unread: 0,
    counterpart: { name: 'M. Bouvier', role: 'Professeur' },
    messages: [
      { id: 'm1', from: 'student', kind: 'audio', preview: 'Enregistrement · 58 sec', at: '2026-02-14T19:10:00Z' },
      { id: 'm2', from: 'teacher', kind: 'text', preview: 'Excellente ouverture. Évite « moi je voudrais », préfère « je voudrais ».', at: '2026-02-15T17:40:00Z' },
    ],
  },
  {
    id: 't3', topic: '💬 Question administrative', updatedAt: '2026-02-12T11:00:00Z', unread: 0,
    counterpart: { name: 'Selora Admin', role: 'Support' },
    messages: [
      { id: 'm1', from: 'student', kind: 'text', preview: 'Bonjour, mon abonnement se renouvelle quand ?', at: '2026-02-12T10:55:00Z' },
      { id: 'm2', from: 'admin', kind: 'text', preview: 'Bonjour Priya, le renouvellement aura lieu le 15 mars.', at: '2026-02-12T11:00:00Z' },
    ],
  },
];

const MOCK_PORTFOLIO = [
  { id: 'p1', title: 'A1 — Au café', date: '2026-01-04', level: 'A1', duration: 45, aiScore: 78, teacherNote: 'Très bien pour un premier essai !' },
  { id: 'p2', title: 'A1 — Demander son chemin', date: '2026-01-09', level: 'A1', duration: 52, aiScore: 82, teacherNote: 'Travaille le « r » dans « rue ».' },
  { id: 'p3', title: 'A2 — Train annulé', date: '2026-01-22', level: 'A2', duration: 78, aiScore: 80, teacherNote: 'Très bonne réaction à l\'imprévu.' },
  { id: 'p4', title: 'A2 — Réservation d\'hôtel', date: '2026-02-17', level: 'A2', duration: 72, aiScore: 84, teacherNote: 'Bel effort ! Travaille la liaison.' },
  { id: 'p5', title: 'A2 — Au restaurant', date: '2026-02-14', level: 'A2', duration: 58, aiScore: 86, teacherNote: 'Excellente ouverture.' },
  { id: 'p6', title: 'B1 — Débat citoyen', date: '2026-02-09', level: 'B1', duration: 142, aiScore: null, teacherNote: 'En attente du retour.' },
];

const MOCK_TEACHER_HOURS = {
  purchased: 20,
  used: 8,
  remaining: 12,
  history: [
    { date: '2026-02-18', teacher: 'Mme Laurent', duration: 30, notes: 'Préparation DELF A2 — section orale.' },
    { date: '2026-02-11', teacher: 'M. Bouvier', duration: 60, notes: 'Travail sur la prononciation des nasales.' },
    { date: '2026-02-04', teacher: 'Mme Laurent', duration: 60, notes: 'Révision passé composé vs imparfait.' },
    { date: '2026-01-28', teacher: 'Mme Laurent', duration: 30, notes: 'Atelier conversation libre.' },
  ],
};

const MOCK_CEFR = [
  { level: 'A1', status: 'done', percent: 100 },
  { level: 'A2', status: 'active', percent: 78 },
  { level: 'B1', status: 'locked', percent: 0 },
  { level: 'B2', status: 'locked', percent: 0 },
];

const MOCK_SKILLS = [
  { name: 'Speaking', icon: Mic, value: 76 },
  { name: 'Listening', icon: Volume2, value: 72 },
  { name: 'Reading', icon: BookOpen, value: 81 },
  { name: 'Writing', icon: MessageSquare, value: 64 },
];

const MOCK_SPEAKING_ANALYTICS = [
  { label: 'Prononciation', value: 82 },
  { label: 'Fluidité', value: 75 },
  { label: 'Vocabulaire', value: 79 },
  { label: 'Grammaire', value: 73 },
  { label: 'Confiance', value: 81 },
];

const MOCK_TIME_STATS = [
  { label: 'Aujourd\'hui', minutes: 38 },
  { label: 'Cette semaine', minutes: 4 * 60 + 22 },
  { label: 'Ce mois-ci', minutes: 18 * 60 + 41 },
  { label: 'Total', minutes: MOCK_PROFILE.speakingMinutes },
];

const MOCK_FEEDBACK = [
  { id: 'f1', kind: 'teacher', from: 'Mme Laurent', at: '2026-02-18', preview: 'Bel effort sur la réservation d\'hôtel. Continue de travailler la liaison.', level: 'A2' },
  { id: 'f2', kind: 'ai', from: 'AI Analysis', at: '2026-02-17', preview: 'Fluidité 79% — pause un peu longue avant « malheureusement ».', level: 'A2' },
  { id: 'f3', kind: 'teacher', from: 'M. Bouvier', at: '2026-02-15', preview: 'Excellente ouverture au restaurant — préfère « je voudrais » à « moi je voudrais ».', level: 'A2' },
  { id: 'f4', kind: 'ai', from: 'AI Analysis', at: '2026-02-14', preview: 'Prononciation 86%, Clarté 84%. Mots à retravailler : addition, dessert.', level: 'A2' },
  { id: 'f5', kind: 'teacher', from: 'Mme Laurent', at: '2026-02-09', preview: 'Pour le débat citoyen B1, structure mieux ton argumentation avec « d\'abord, ensuite, enfin ».', level: 'B1' },
];

const MOCK_CLUB_EVENTS = [
  { id: 'e1', title: 'Soirée Conversation — Niveau A2', date: '2026-02-22T19:00:00Z', duration: 60, host: 'Mme Laurent', spots: 8, taken: 5 },
  { id: 'e2', title: 'Discussion Live : Cinéma français contemporain', date: '2026-02-25T18:30:00Z', duration: 75, host: 'M. Bouvier', spots: 12, taken: 9 },
  { id: 'e3', title: 'Challenge mensuel — Raconte ton week-end', date: '2026-03-01T00:00:00Z', duration: null, host: 'Selora Club', spots: null, taken: null },
];

const STREAK_REWARDS = [
  { days: 15, reward: 'Session individuelle avec un professeur (15 minutes)', kind: 'teacher', detail: 'Pratique orale · prononciation · questions-réponses' },
  { days: 30, reward: 'Accès au Club Francophone pendant 1 semaine', kind: 'club', detail: 'Sessions de conversation · discussions communautaires · événements spéciaux' },
  { days: 45, reward: 'Session individuelle avec un professeur (15 minutes)', kind: 'teacher', detail: 'Approfondissement personnalisé' },
  { days: 60, reward: 'Accès au Club Francophone pendant 2 semaines', kind: 'club', detail: 'Conversation immersive prolongée' },
  { days: 75, reward: 'Session individuelle avec un professeur (30 minutes)', kind: 'teacher', detail: 'Coaching ciblé approfondi' },
  { days: 90, reward: 'Évaluation orale personnalisée', kind: 'evaluation', detail: 'Rapport prononciation · évaluation fluidité · recommandations vocabulaire' },
  { days: 120, reward: 'Accès VIP au Club Francophone pendant 1 mois', kind: 'club', detail: 'Club premium · événements privés' },
  { days: 150, reward: 'Session Premium avec un professeur (30 minutes)', kind: 'teacher', detail: 'Coaching avancé personnalisé' },
  { days: 180, reward: 'Simulation complète DELF orale', kind: 'evaluation', detail: 'Simulation officielle avec retour détaillé du professeur' },
];

const SPEAKING_HOURS_REWARDS = [
  { hours: 25, reward: 'Analyse orale prioritaire', kind: 'evaluation' },
  { hours: 50, reward: 'Session professeur de 15 minutes', kind: 'teacher' },
  { hours: 75, reward: 'Accès Club Francophone pendant 1 semaine', kind: 'club' },
  { hours: 100, reward: 'Session professeur de 30 minutes', kind: 'teacher' },
  { hours: 150, reward: 'Évaluation orale avancée', kind: 'evaluation' },
];

const LEVEL_REWARDS = [
  { level: 'A1', status: 'done', certificate: 'Certificat A1', bonus: 'Défi conversation spécial' },
  { level: 'A2', status: 'active', certificate: 'Certificat A2', bonus: 'Évaluation orale A2' },
  { level: 'B1', status: 'locked', certificate: 'Certificat B1', bonus: 'Simulation DELF B1 orale' },
  { level: 'B2', status: 'locked', certificate: 'Certificat B2', bonus: 'Simulation DELF B2 orale' },
];

const ACHIEVEMENT_LEVELS = [
  { id: 'bronze', name: 'Bronze', threshold: 15, color: '#B87333', benefits: ['Badge spécial'] },
  { id: 'silver', name: 'Argent', threshold: 45, color: '#A7A7AD', benefits: ['Feedback prioritaire', 'Défis exclusifs'] },
  { id: 'gold', name: 'Or', threshold: 90, color: '#D4AF37', benefits: ['Réponses professeur prioritaires', 'Accès aux événements premium'] },
  { id: 'platinum', name: 'Platine', threshold: 180, color: '#1A2F4A', benefits: ['Accès VIP au Club Francophone', 'Événements oraux spéciaux'] },
];

const MOCK_REWARDS = {
  currentStreak: MOCK_PROFILE.streakDays,
  speakingMinutes: MOCK_PROFILE.speakingMinutes,
  history: [
    { id: 'h1', reward: 'Session Professeur (15 min)', kind: 'teacher', at: '2026-01-04', status: 'utilisée' },
    { id: 'h2', reward: 'Accès Club Francophone (1 semaine)', kind: 'club', at: '2026-01-20', status: 'utilisée' },
    { id: 'h3', reward: 'Analyse orale prioritaire', kind: 'evaluation', at: '2026-02-02', status: 'disponible' },
    { id: 'h4', reward: 'Session Professeur (15 min)', kind: 'teacher', at: '2026-02-10', status: 'disponible' },
  ],
};

const MOCK_BILLING = {
  plan: 'Selora Premium · Mensuel',
  price: 39,
  currency: '€',
  renewalDate: '2026-03-15',
  invoices: [
    { id: 'inv-2602', date: '2026-02-15', amount: 39, status: 'Payée' },
    { id: 'inv-2502', date: '2026-01-15', amount: 39, status: 'Payée' },
    { id: 'inv-2412', date: '2025-12-15', amount: 39, status: 'Payée' },
    { id: 'inv-h20', date: '2026-01-28', amount: 240, status: 'Payée', note: 'Pack 20h professeur' },
  ],
  promoActive: null,
};

const MOCK_NOTIFICATIONS = [
  { id: 'n1', icon: '👩‍🏫', text: 'Nouveau retour de Mme Laurent sur « Réservation d\'hôtel ».', at: '2026-02-18T09:24:00Z', unread: true },
  { id: 'n2', icon: '🔥', text: 'Plus que 12 jours avant ta prochaine récompense (Accès Club Francophone — 30 jours).', at: '2026-02-18T07:00:00Z', unread: true },
  { id: 'n3', icon: '🇫🇷', text: 'Événement Club Francophone demain à 19h : Soirée Conversation A2.', at: '2026-02-17T20:00:00Z', unread: false },
  { id: 'n4', icon: '🎤', text: 'Tu n\'as pas pratiqué l\'expression libre depuis 4 jours.', at: '2026-02-16T08:00:00Z', unread: false },
  { id: 'n5', icon: '🎁', text: 'Tu as débloqué une session professeur de 30 min.', at: '2026-02-10T11:00:00Z', unread: false },
];

/* ===========================================================
   HELPERS
=========================================================== */
const minutesToHM = (m) => {
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r} min`;
  return r === 0 ? `${h}h` : `${h}h ${r} min`;
};

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatRelative = (iso) => {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'à l\'instant';
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `il y a ${Math.floor(diff / 86400)} j`;
  return formatDate(iso);
};

/* ===========================================================
   SHARED PRIMITIVES
=========================================================== */
const SectionTitle = ({ children, eyebrow }) => (
  <div className="mb-6">
    {eyebrow && (
      <p className="text-[10px] tracking-[0.3em] font-bold text-[#D4AF37] uppercase mb-1">{eyebrow}</p>
    )}
    <h2 className="text-2xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
      {children}
    </h2>
  </div>
);

const Card = ({ children, className = '', testid }) => (
  <div className={`bg-white border border-[#E8E4DC] rounded-2xl ${className}`} data-testid={testid}>
    {children}
  </div>
);

const Progress = ({ value, color = '#D4AF37' }) => (
  <div className="w-full h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${value}%`, background: color }} />
  </div>
);

/* ===========================================================
   SIDEBAR
=========================================================== */
const SIDEBAR_ITEMS = [
  { id: 'home', label: 'Accueil', icon: Home },
  { id: 'messages', label: 'Messages', icon: MessageCircle, priority: true },
  { id: 'portfolio', label: 'Portfolio oral', icon: Mic, priority: true },
  { id: 'progression', label: 'Ma progression', icon: TrendingUp, priority: true },
  { id: 'teacher-hours', label: 'Heures professeur', icon: GraduationCap },
  { id: 'feedback', label: 'Feedback', icon: MessageSquare },
  { id: 'club', label: 'Club Francophone', icon: Globe },
  { id: 'rewards', label: 'Mes Récompenses', icon: Gift },
  { id: 'billing', label: 'Abonnement', icon: CreditCard },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'settings', label: 'Paramètres', icon: Settings },
];

function Sidebar({ active, setActive, unread }) {
  return (
    <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-[#E8E4DC] min-h-screen sticky top-0 py-8 px-4" data-testid="dashboard-sidebar">
      <p className="px-3 text-[10px] tracking-[0.3em] font-bold text-[#6B4E37] uppercase mb-4">Espace étudiant</p>
      <nav className="space-y-0.5 flex-1">
        {SIDEBAR_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = active === item.id;
          const badge = item.id === 'messages' ? unread : 0;
          return (
            <button
              key={item.id}
              onClick={() => setActive(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                isActive ? 'bg-[#1A2F4A] text-white' : 'text-[#1A2F4A] hover:bg-[#FAF7F0]'
              }`}
              data-testid={`sidebar-${item.id}`}
            >
              <Icon className={`w-4 h-4 ${isActive ? 'text-[#D4AF37]' : 'text-[#6B4E37]'}`} />
              <span className="flex-1 text-left">{item.label}</span>
              {badge > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-[#D4AF37] text-[#1A2F4A]' : 'bg-[#D4AF37] text-[#1A2F4A]'}`}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

/* Mobile section selector */
function MobileSectionSelect({ active, setActive }) {
  return (
    <div className="lg:hidden bg-white border-b border-[#E8E4DC] sticky top-0 z-10 py-3 px-4" data-testid="dashboard-mobile-nav">
      <select
        value={active}
        onChange={(e) => setActive(e.target.value)}
        className="w-full bg-[#FAF7F0] border border-[#E8E4DC] rounded-lg px-3 py-2 text-sm text-[#1A2F4A] font-medium"
        data-testid="mobile-section-select"
      >
        {SIDEBAR_ITEMS.map((it) => (
          <option key={it.id} value={it.id}>{it.label}</option>
        ))}
      </select>
    </div>
  );
}

/* ===========================================================
   SECTION — HOME
=========================================================== */
function HomeSection({ user, setActive, summary }) {
  const firstName = (user?.full_name || 'Apprenant(e)').split(' ')[0];
  const p = summary?.progress || {};
  const streakDays = p.streak_days ?? 0;
  const totalCompleted = p.total_completed ?? 0;
  const speakingMinutes = Math.round((p.speaking_seconds || 0) / 60);
  const displayLevel = user?.level || (p.by_level || []).find(x => x.status === 'active')?.level || 'A1';
  const stats = [
    { label: 'Niveau', value: `🇫🇷 ${displayLevel}`, icon: BookOpen, testid: 'stat-level' },
    { label: 'Série en cours', value: `🔥 ${streakDays} jour${streakDays > 1 ? 's' : ''}`, icon: Flame, testid: 'stat-streak' },
    { label: 'Temps de parole', value: minutesToHM(speakingMinutes), icon: Mic, testid: 'stat-speaking' },
    { label: 'Activités terminées', value: totalCompleted, icon: CheckCircle2, testid: 'stat-completed' },
  ];

  return (
    <div className="space-y-8" data-testid="section-home">
      {/* Welcome */}
      <Card className="px-8 py-6 bg-gradient-to-br from-[#1A2F4A] to-[#243a58] text-white border-0" testid="welcome-card">
        <p className="text-[10px] tracking-[0.3em] font-bold text-[#D4AF37] uppercase mb-2">Espace étudiant</p>
        <h1 className="text-3xl sm:text-4xl font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="welcome-greeting">
          Bonjour, {firstName} <span className="text-[#D4AF37]">👋</span>
        </h1>
        <p className="text-sm text-white/80 italic">Bienvenue sur votre espace d'apprentissage.</p>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stats-grid">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.label} className="px-4 py-4" testid={s.testid}>
              <p className="text-[10px] tracking-[0.25em] font-bold text-[#6B4E37] uppercase mb-1.5 flex items-center gap-1.5">
                <Icon className="w-3 h-3 text-[#D4AF37]" /> {s.label}
              </p>
              <p className="text-xl sm:text-2xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                {s.value}
              </p>
            </Card>
          );
        })}
      </div>

      {/* Continue learning */}
      <div>
        <SectionTitle eyebrow="Continuer">Reprendre où tu t'es arrêté(e)</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="continue-learning">
          {MOCK_CONTINUE.map((c) => (
            <Link
              key={c.id}
              to={c.href}
              className="group bg-white border border-[#E8E4DC] rounded-2xl px-4 py-4 hover:border-[#D4AF37] transition-colors"
              data-testid={`continue-${c.id}`}
            >
              <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-1">{c.module}</p>
              <p className="text-sm font-bold text-[#1A2F4A] mb-2 leading-tight">{c.activity}</p>
              <div className="flex items-center gap-2">
                <Progress value={c.progress} />
                <span className="text-[10px] text-[#6B4E37] font-bold whitespace-nowrap">{c.progress}%</span>
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs text-[#1A2F4A] font-bold group-hover:text-[#D4AF37]">
                <Play className="w-3 h-3" /> Reprendre
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Priority quick cards */}
      <div>
        <SectionTitle eyebrow="Tes priorités">Espaces essentiels</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="priority-cards">
          {[
            { id: 'messages', name: 'Message Centre', icon: MessageCircle, desc: 'Conversations professeur + admin', badge: MOCK_THREADS.reduce((s, t) => s + t.unread, 0) },
            { id: 'portfolio', name: 'Portfolio oral', icon: Mic, desc: 'Tous tes enregistrements + retours', badge: 0 },
            { id: 'progression', name: 'Ma progression', icon: TrendingUp, desc: 'CEFR · Compétences · Temps', badge: 0 },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                className="group bg-white border border-[#E8E4DC] rounded-2xl px-5 py-5 text-left hover:border-[#D4AF37] transition-colors relative"
                data-testid={`priority-${c.id}`}
              >
                {c.badge > 0 && (
                  <span className="absolute top-3 right-3 text-[10px] font-bold bg-[#D4AF37] text-[#1A2F4A] rounded-full px-1.5 py-0.5">{c.badge}</span>
                )}
                <Icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                <h3 className="text-base font-bold text-[#1A2F4A] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{c.name}</h3>
                <p className="text-xs text-[#6B4E37] leading-relaxed">{c.desc}</p>
                <span className="inline-flex items-center gap-1 text-xs text-[#1A2F4A] font-bold mt-3 group-hover:text-[#D4AF37]">
                  Ouvrir <ArrowUpRight className="w-3 h-3" />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Secondary quick cards */}
      <div>
        <SectionTitle eyebrow="Plus">Tout ton parcours</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="secondary-cards">
          {[
            { id: 'teacher-hours', name: 'Heures prof.', icon: GraduationCap },
            { id: 'rewards', name: 'Mes Récompenses', icon: Gift },
            { id: 'club', name: 'Club Francophone', icon: Globe },
            { id: 'billing', name: 'Abonnement', icon: CreditCard },
            { id: 'feedback', name: 'Feedback', icon: MessageSquare },
            { id: 'notifications', name: 'Notifications', icon: Bell },
            { id: 'settings', name: 'Paramètres', icon: Settings },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                className="bg-white border border-[#E8E4DC] rounded-xl px-4 py-3 text-left hover:border-[#D4AF37] transition-colors"
                data-testid={`secondary-${c.id}`}
              >
                <Icon className="w-5 h-5 text-[#D4AF37] mb-2" />
                <p className="text-xs font-bold text-[#1A2F4A]">{c.name}</p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ===========================================================
   SECTION — MESSAGES
=========================================================== */
function MessagesSection() {
  const [activeId, setActiveId] = useState(MOCK_THREADS[0].id);
  const [filter, setFilter] = useState('all');
  const [draft, setDraft] = useState('');

  const filtered = MOCK_THREADS.filter((t) => {
    if (filter === 'unread') return t.unread > 0;
    if (filter === 'teacher') return t.counterpart.role.toLowerCase().includes('professeur');
    if (filter === 'admin') return t.counterpart.role.toLowerCase().includes('admin') || t.counterpart.role.toLowerCase().includes('support');
    return true;
  });
  const active = MOCK_THREADS.find((t) => t.id === activeId) || MOCK_THREADS[0];

  return (
    <div className="space-y-6" data-testid="section-messages">
      <SectionTitle eyebrow="Message Centre">Conversations avec ton professeur et l'équipe</SectionTitle>

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="message-quick-actions">
        {[
          { id: 'msg-teacher', label: 'Écrire au professeur', icon: GraduationCap },
          { id: 'msg-admin', label: 'Écrire à l\'admin', icon: Settings },
          { id: 'msg-support', label: 'Contacter le support', icon: MessageSquare },
        ].map((a) => {
          const Icon = a.icon;
          return (
            <button
              key={a.id}
              className="bg-white border border-[#E8E4DC] hover:border-[#D4AF37] rounded-xl px-4 py-3 text-left transition-colors inline-flex items-center gap-2"
              data-testid={a.id}
            >
              <Icon className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs font-bold text-[#1A2F4A]">{a.label}</span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
        {/* Thread list */}
        <Card className="overflow-hidden" testid="thread-list">
          <div className="px-3 py-2 border-b border-[#E8E4DC] flex gap-1 text-[10px] font-bold uppercase tracking-wider">
            {[
              { id: 'all', label: 'Tous' },
              { id: 'unread', label: 'Non lus' },
              { id: 'teacher', label: 'Prof.' },
              { id: 'admin', label: 'Admin' },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-2 py-1 rounded ${filter === f.id ? 'bg-[#1A2F4A] text-white' : 'text-[#6B4E37] hover:bg-[#FAF7F0]'}`}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {filtered.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveId(t.id)}
                className={`w-full text-left px-3 py-3 border-b border-[#F3F4F6] hover:bg-[#FAF7F0] transition-colors ${activeId === t.id ? 'bg-[#FAF7F0]' : ''}`}
                data-testid={`thread-tile-${t.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-bold text-[#1A2F4A] truncate">{t.topic}</p>
                  {t.unread > 0 && <span className="text-[10px] font-bold bg-[#D4AF37] text-[#1A2F4A] rounded-full px-1.5 py-0.5">{t.unread}</span>}
                </div>
                <p className="text-[10px] text-[#6B4E37] truncate">{t.counterpart.name} · {t.counterpart.role}</p>
                <p className="text-[10px] text-[#999] mt-0.5">{formatRelative(t.updatedAt)}</p>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-6 text-xs text-[#6B4E37] italic text-center">Aucune conversation.</p>
            )}
          </div>
        </Card>

        {/* Active thread */}
        <Card className="flex flex-col overflow-hidden" testid="active-thread">
          <div className="px-5 py-3 border-b border-[#E8E4DC]">
            <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase">{active.counterpart.role}</p>
            <p className="text-sm font-bold text-[#1A2F4A]">{active.topic} — {active.counterpart.name}</p>
          </div>
          <div className="flex-1 px-5 py-4 space-y-3 max-h-[420px] overflow-y-auto" data-testid="thread-messages">
            {active.messages.map((m) => {
              const fromStudent = m.from === 'student';
              const bg = fromStudent ? 'bg-[#1A2F4A] text-white' : m.from === 'ai' ? 'bg-[#FAF7F0] text-[#1A2F4A] border border-[#E8E4DC]' : 'bg-white border border-[#E8E4DC] text-[#1A2F4A]';
              const align = fromStudent ? 'justify-end' : 'justify-start';
              return (
                <div key={m.id} className={`flex ${align}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${bg}`} data-testid={`message-${m.id}`}>
                    <p className="text-[10px] uppercase tracking-wider font-bold mb-1 opacity-70">{m.from === 'student' ? 'Toi' : m.from === 'ai' ? 'AI Analysis' : m.from === 'teacher' ? active.counterpart.name : 'Admin'}{m.kind === 'audio' && ' · 🎤'}</p>
                    <p className="text-[13px] leading-snug">{m.preview}</p>
                    <p className="text-[10px] mt-1 opacity-60">{formatRelative(m.at)}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="px-5 py-3 border-t border-[#E8E4DC] bg-[#FAF7F0]/40">
            <div className="flex items-center gap-2">
              <button className="text-[#6B4E37] hover:text-[#D4AF37]" data-testid="attach-btn"><Paperclip className="w-4 h-4" /></button>
              <button className="text-[#6B4E37] hover:text-[#D4AF37]" data-testid="record-audio-msg"><Mic className="w-4 h-4" /></button>
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Écris un message…"
                className="flex-1 bg-white border border-[#E8E4DC] rounded-full px-3 py-1.5 text-xs text-[#1A2F4A] focus:border-[#D4AF37] outline-none"
                data-testid="message-input"
              />
              <button className="bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1" data-testid="send-message">
                <Send className="w-3 h-3" /> Envoyer
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ===========================================================
   SECTION — PORTFOLIO (Oral Portfolio, real R2 recordings)
=========================================================== */
function PortfolioSection() {
  const t = useT();
  const [levelFilter, setLevelFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    Promise.all([listMyRecordings(), mySubmissions()])
      .then(([recData, subs]) => {
        if (!alive) return;
        const subById = Object.fromEntries((subs || []).map((s) => [s.id, s]));
        const merged = (recData?.items || []).map((r) => ({
          ...r,
          submission: subById[r.submission_id] || null,
        }));
        setItems(merged);
      })
      .catch((err) => alive && setError(err.message || 'Impossible de charger le portfolio.'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const filtered = items.filter((r) => levelFilter === 'all' || r.level === levelFilter);

  return (
    <div className="space-y-6" data-testid="section-portfolio">
      <SectionTitle eyebrow="Portfolio oral">
        {t({ fr: 'Tous tes enregistrements et retours professeur', en: 'All your recordings and teacher feedback' })}
      </SectionTitle>

      {/* Filters */}
      <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
        {['all', 'A1', 'A2', 'B1', 'B2'].map((lv) => (
          <button
            key={lv}
            onClick={() => setLevelFilter(lv)}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border ${levelFilter === lv ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]' : 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37]'}`}
            data-testid={`portfolio-filter-${lv}`}
          >
            {lv === 'all' ? t({ fr: 'Tous', en: 'All' }) : lv}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="px-3 py-6 text-xs text-[#6B4E37] italic" data-testid="portfolio-loading">
          {t({ fr: 'Chargement de tes enregistrements…', en: 'Loading your recordings…' })}
        </p>
      ) : error ? (
        <p className="px-3 py-6 text-xs text-red-600" data-testid="portfolio-error">{error}</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#E8E4DC] rounded-2xl px-6 py-10 text-center" data-testid="portfolio-empty">
          <Mic className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
          <p className="text-sm font-bold text-[#1A2F4A] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t({ fr: 'Aucun enregistrement à ce niveau', en: 'No recordings at this level' })}
          </p>
          <p className="text-xs text-[#6B4E37] italic">
            {t({
              fr: 'Rends-toi dans le Speaking Lab, enregistre-toi et envoie ta production à ton professeur pour la voir apparaître ici.',
              en: 'Head to the Speaking Lab, record yourself, and send your work to your teacher to see it appear here.',
            })}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="portfolio-grid">
          {filtered.map((r) => (
            <PortfolioCard key={r.id} rec={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function PortfolioCard({ rec }) {
  const t = useT();
  const sub = rec.submission;
  const reviewed = sub?.status === 'reviewed';
  const fb = sub?.feedback || {};
  const created = new Date(rec.created_at);
  const durationLabel = rec.duration_seconds != null ? `${Math.round(rec.duration_seconds)}s` : '—';

  return (
    <Card className="px-5 py-4" testid={`portfolio-${rec.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase">
            {rec.level} · {formatDate(created.toISOString())}
          </p>
          <p className="text-base font-bold text-[#1A2F4A] mt-0.5 truncate" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="portfolio-title">
            {rec.exercise_title}
          </p>
          {rec.prompt_text && (
            <p className="text-[11px] text-[#6B4E37] italic truncate" data-testid="portfolio-prompt">
              "{rec.prompt_text}"
            </p>
          )}
        </div>
        <span className="text-[10px] text-[#6B4E37] font-bold whitespace-nowrap ml-2">{durationLabel}</span>
      </div>

      <audio
        controls
        src={rec.playback_url}
        className="w-full mt-2 mb-3"
        data-testid={`portfolio-audio-${rec.id}`}
      />

      {reviewed ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid={`portfolio-feedback-${rec.id}`}>
          <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-1">
            {t({ fr: 'Retour du professeur', en: 'Teacher feedback' })}
          </p>
          {(fb.corrections || fb.feedback) && (
            <p className="text-[11px] text-emerald-900 mb-1">
              {fb.feedback || fb.corrections}
            </p>
          )}
          {(fb.advice || fb.conseils || fb.explanation || fb.comment) && (
            <p className="text-[11px] text-emerald-900 italic">
              {fb.advice || fb.conseils || fb.explanation || fb.comment}
            </p>
          )}
          {(fb.score_total != null || fb.score != null) && (
            <p className="text-[11px] font-bold text-emerald-900 mt-1">
              {t({ fr: 'Note', en: 'Score' })}: {fb.score_total ?? fb.score}/20
            </p>
          )}
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid={`portfolio-pending-${rec.id}`}>
          <Clock className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
          <p className="text-[11px] text-amber-800">
            {t({ fr: 'En attente du retour de ton professeur…', en: 'Waiting for your teacher\u2019s feedback…' })}
          </p>
        </div>
      )}
    </Card>
  );
}

/* ===========================================================
   SECTION — TEACHER HOURS
=========================================================== */
function TeacherHoursSection() {
  const { purchased, used, remaining, history } = MOCK_TEACHER_HOURS;
  const pct = (used / purchased) * 100;

  return (
    <div className="space-y-6" data-testid="section-teacher-hours">
      <SectionTitle eyebrow="Heures professeur">Solde, réservations et historique</SectionTitle>

      {/* Balance */}
      <Card className="px-6 py-5" testid="teacher-balance">
        <div className="grid grid-cols-3 gap-4 mb-4">
          {[
            { label: 'Achetées', value: `${purchased} h` },
            { label: 'Utilisées', value: `${used} h` },
            { label: 'Restantes', value: `${remaining} h`, highlight: true },
          ].map((b) => (
            <div key={b.label}>
              <p className="text-[10px] tracking-[0.25em] font-bold text-[#6B4E37] uppercase mb-1">{b.label}</p>
              <p className={`text-2xl font-bold ${b.highlight ? 'text-[#D4AF37]' : 'text-[#1A2F4A]'}`} style={{ fontFamily: "'Playfair Display', serif" }} data-testid={`balance-${b.label}`}>
                {b.value}
              </p>
            </div>
          ))}
        </div>
        <Progress value={pct} />
        <p className="text-[10px] text-[#6B4E37] italic mt-2">{used} h utilisées sur {purchased} h ({Math.round(pct)}%)</p>
      </Card>

      {/* Book session */}
      <div>
        <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-2">Réserver une session</p>
        <div className="grid grid-cols-4 gap-2" data-testid="booking-options">
          {[15, 30, 60, 90].map((m) => (
            <button
              key={m}
              className="bg-white border border-[#E8E4DC] hover:border-[#D4AF37] rounded-xl px-3 py-3 text-center transition-colors"
              data-testid={`book-${m}`}
            >
              <p className="text-lg font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>{m} min</p>
              <p className="text-[10px] text-[#6B4E37]">Réserver</p>
            </button>
          ))}
        </div>
      </div>

      {/* Purchase CTA */}
      <Card className="px-6 py-5 bg-gradient-to-br from-[#FAF7F0] to-white" testid="purchase-cta">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm font-bold text-[#1A2F4A]">Besoin d'heures supplémentaires ?</p>
            <p className="text-[11px] text-[#6B4E37]">Pack 10h, 20h ou 50h — tarifs dégressifs.</p>
          </div>
          <button className="bg-[#D4AF37] hover:bg-[#1A2F4A] hover:text-[#D4AF37] text-[#1A2F4A] rounded-full px-5 py-2 text-xs font-bold transition-colors" data-testid="buy-more-hours">
            Acheter plus d'heures
          </button>
        </div>
      </Card>

      {/* History */}
      <div>
        <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-2">Historique des sessions</p>
        <Card className="overflow-hidden" testid="session-history">
          <div className="divide-y divide-[#F3F4F6]">
            {history.map((h, i) => (
              <div key={i} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`history-${i}`}>
                <div>
                  <p className="text-xs font-bold text-[#1A2F4A]">{h.teacher} · {h.duration} min</p>
                  <p className="text-[11px] text-[#6B4E37] italic mt-0.5">{h.notes}</p>
                </div>
                <p className="text-[10px] text-[#999] whitespace-nowrap">{formatDate(h.date)}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ===========================================================
   SECTION — PROGRESSION
=========================================================== */
function ProgressionSection({ summary }) {
  const t = useT();
  const byLevel = (summary?.progress?.by_level && summary.progress.by_level.length > 0)
    ? summary.progress.by_level
    : [
        { level: 'A1', count: 0, target: 40, percent: 0, status: 'active' },
        { level: 'A2', count: 0, target: 60, percent: 0, status: 'locked' },
        { level: 'B1', count: 0, target: 80, percent: 0, status: 'locked' },
        { level: 'B2', count: 0, target: 100, percent: 0, status: 'locked' },
      ];

  return (
    <div className="space-y-6" data-testid="section-progression">
      <SectionTitle eyebrow="Ma progression">
        {t({ fr: 'CEFR · Compétences · Statistiques', en: 'CEFR · Skills · Statistics' })}
      </SectionTitle>

      {/* CEFR */}
      <div>
        <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-2">
          {t({ fr: 'Parcours CEFR', en: 'CEFR Path' })}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cefr-grid">
          {byLevel.map((c) => {
            const isLocked = c.status === 'locked';
            const isDone = c.status === 'done';
            const isActive = c.status === 'active';
            return (
              <Card key={c.level} className={`px-4 py-4 ${isActive ? 'border-[#D4AF37]' : ''}`} testid={`cefr-${c.level}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-2xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>{c.level}</p>
                  {isLocked && <Lock className="w-3.5 h-3.5 text-[#6B4E37]" />}
                  {isDone && <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />}
                </div>
                {isLocked ? (
                  <p className="text-[10px] text-[#6B4E37] italic">{t({ fr: 'Verrouillé', en: 'Locked' })}</p>
                ) : (
                  <>
                    <Progress value={c.percent} />
                    <p className="text-[10px] text-[#6B4E37] mt-1.5 font-bold" data-testid={`cefr-${c.level}-percent`}>
                      {c.percent}% <span className="opacity-60 font-normal">· {c.count}/{c.target}</span>
                    </p>
                  </>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Skills */}
      <div>
        <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-2">Compétences</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="skills-grid">
          {MOCK_SKILLS.map((s) => {
            const Icon = s.icon;
            return (
              <Card key={s.name} className="px-4 py-4" testid={`skill-${s.name}`}>
                <p className="text-[10px] tracking-[0.25em] font-bold text-[#6B4E37] uppercase mb-1.5 flex items-center gap-1.5">
                  <Icon className="w-3 h-3 text-[#D4AF37]" /> {s.name}
                </p>
                <Progress value={s.value} />
                <p className="text-[10px] text-[#6B4E37] mt-1.5 font-bold">{s.value}%</p>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Speaking Analytics */}
      <div>
        <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-2">Analyse Speaking</p>
        <Card className="px-5 py-5" testid="speaking-analytics">
          <div className="space-y-3">
            {MOCK_SPEAKING_ANALYTICS.map((m) => (
              <div key={m.label} data-testid={`analytics-${m.label}`}>
                <div className="flex justify-between text-[11px] text-[#1A2F4A] mb-1 font-bold">
                  <span>{m.label}</span>
                  <span className="text-[#D4AF37]">{m.value}%</span>
                </div>
                <Progress value={m.value} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Time stats */}
      <div>
        <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-2">Temps de parole</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="time-stats">
          {MOCK_TIME_STATS.map((t) => (
            <Card key={t.label} className="px-4 py-4" testid={`time-${t.label}`}>
              <p className="text-[10px] tracking-[0.25em] font-bold text-[#6B4E37] uppercase mb-1">{t.label}</p>
              <p className="text-xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>{minutesToHM(t.minutes)}</p>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ===========================================================
   SECTION — FEEDBACK
=========================================================== */
function FeedbackSection() {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const filtered = MOCK_FEEDBACK.filter((f) => {
    if (filter !== 'all' && f.kind !== filter) return false;
    if (query && !f.preview.toLowerCase().includes(query.toLowerCase()) && !f.from.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="section-feedback">
      <SectionTitle eyebrow="Feedback Center">Tous les retours professeur et AI</SectionTitle>

      <div className="flex flex-wrap items-center gap-2" data-testid="feedback-filters">
        {[
          { id: 'all', label: 'Tous' },
          { id: 'teacher', label: 'Professeur' },
          { id: 'ai', label: 'AI Analysis' },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border ${filter === f.id ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]' : 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37]'}`}
            data-testid={`feedback-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
        <div className="flex-1 min-w-[200px] flex items-center gap-2 bg-white border border-[#E8E4DC] rounded-full px-3 py-1.5">
          <Search className="w-3.5 h-3.5 text-[#6B4E37]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher dans les retours…"
            className="flex-1 text-xs outline-none text-[#1A2F4A]"
            data-testid="feedback-search"
          />
        </div>
      </div>

      <div className="space-y-2" data-testid="feedback-list">
        {filtered.map((f) => (
          <Card key={f.id} className="px-4 py-3 flex items-start gap-3" testid={`feedback-${f.id}`}>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${f.kind === 'teacher' ? 'bg-[#1A2F4A] text-white' : 'bg-[#FAF7F0] text-[#1A2F4A] border border-[#E8E4DC]'}`}>
              {f.kind === 'teacher' ? '👩‍🏫 Prof' : '🤖 AI'}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs font-bold text-[#1A2F4A]">{f.from} · {f.level}</p>
                <p className="text-[10px] text-[#999]">{formatDate(f.at)}</p>
              </div>
              <p className="text-[12px] text-[#1A2F4A]/90 mt-1 leading-relaxed">{f.preview}</p>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && <p className="px-3 py-6 text-xs text-[#6B4E37] italic">Aucun retour ne correspond à ta recherche.</p>}
      </div>
    </div>
  );
}

/* ===========================================================
   SECTION — CLUB
=========================================================== */
function ClubSection() {
  return (
    <div className="space-y-6" data-testid="section-club">
      <SectionTitle eyebrow="Francophone Club">Conversations live et événements communautaires</SectionTitle>

      <Card className="px-6 py-5 bg-gradient-to-br from-[#1A2F4A] to-[#243a58] text-white border-0" testid="club-hero">
        <p className="text-[10px] tracking-[0.3em] font-bold text-[#D4AF37] uppercase mb-2">Le Club Francophone</p>
        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Pratique le français avec d'autres apprenants</h3>
        <p className="text-sm text-white/80 italic mb-4">Soirées conversation, discussions live, challenges mensuels et annonces du club.</p>
        <div className="flex flex-wrap gap-2">
          <button className="bg-[#D4AF37] hover:bg-white text-[#1A2F4A] rounded-full px-5 py-2 text-xs font-bold transition-colors" data-testid="join-club">
            Rejoindre le Club
          </button>
          <button className="border border-white/30 hover:bg-white/10 text-white rounded-full px-5 py-2 text-xs font-bold transition-colors" data-testid="book-conversation">
            Réserver une conversation
          </button>
        </div>
      </Card>

      <div>
        <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-2">Événements à venir</p>
        <div className="space-y-2" data-testid="club-events">
          {MOCK_CLUB_EVENTS.map((e) => (
            <Card key={e.id} className="px-5 py-4" testid={`event-${e.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-1">{formatDate(e.date)}{e.duration ? ` · ${e.duration} min` : ''}</p>
                  <p className="text-sm font-bold text-[#1A2F4A]">{e.title}</p>
                  <p className="text-[11px] text-[#6B4E37] italic mt-1">Animé par {e.host}{e.spots !== null ? ` · ${e.taken}/${e.spots} participants` : ''}</p>
                </div>
                <button className="bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-full px-4 py-1.5 text-xs font-bold" data-testid={`join-event-${e.id}`}>
                  Participer
                </button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ===========================================================
   SECTION — REWARDS
=========================================================== */
function RewardKindBadge({ kind }) {
  const map = {
    teacher: { label: '👩‍🏫 Professeur', cls: 'bg-[#1A2F4A] text-white' },
    club: { label: '🇫🇷 Club', cls: 'bg-[#D4AF37] text-[#1A2F4A]' },
    evaluation: { label: '🎤 Évaluation', cls: 'bg-[#FAF7F0] text-[#1A2F4A] border border-[#E8E4DC]' },
  };
  const m = map[kind] || map.evaluation;
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

function RewardsSection() {
  const [tab, setTab] = useState('streak');
  const streak = MOCK_REWARDS.currentStreak;
  const speakingHours = MOCK_REWARDS.speakingMinutes / 60;

  // Find next streak reward
  const nextStreakReward = STREAK_REWARDS.find((r) => r.days > streak) || STREAK_REWARDS[STREAK_REWARDS.length - 1];
  const prevThreshold = [...STREAK_REWARDS].reverse().find((r) => r.days <= streak)?.days || 0;
  const streakPct = ((streak - prevThreshold) / (nextStreakReward.days - prevThreshold)) * 100;
  const daysLeft = Math.max(0, nextStreakReward.days - streak);

  // Find next hours reward
  const nextHoursReward = SPEAKING_HOURS_REWARDS.find((r) => r.hours > speakingHours) || SPEAKING_HOURS_REWARDS[SPEAKING_HOURS_REWARDS.length - 1];
  const hoursLeft = Math.max(0, nextHoursReward.hours - speakingHours).toFixed(1);

  // Current achievement tier
  const currentTier = [...ACHIEVEMENT_LEVELS].reverse().find((a) => a.threshold <= streak);
  const nextTier = ACHIEVEMENT_LEVELS.find((a) => a.threshold > streak);

  return (
    <div className="space-y-6" data-testid="section-rewards">
      <SectionTitle eyebrow="Mes Récompenses">Streaks · Heures · Niveaux · Achievements</SectionTitle>

      {/* Current Streak hero */}
      <Card className="px-6 py-6 bg-gradient-to-br from-[#1A2F4A] to-[#243a58] text-white border-0" testid="streak-card">
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
          <div className="text-center">
            <Flame className="w-10 h-10 text-[#D4AF37] mx-auto mb-1" />
            <p className="text-5xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="current-streak">{streak}</p>
            <p className="text-[10px] text-white/70 uppercase tracking-wider font-bold">jours consécutifs</p>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-1">Prochaine récompense</p>
            <p className="text-base font-bold mb-1" data-testid="next-reward-title">🎁 {nextStreakReward.reward}</p>
            <p className="text-[11px] text-white/70 italic mb-3">{nextStreakReward.detail}</p>
            <div className="flex items-center gap-2 mb-1">
              <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-[#D4AF37] transition-all duration-700" style={{ width: `${streakPct}%` }} />
              </div>
              <span className="text-[10px] font-bold text-[#D4AF37] whitespace-nowrap" data-testid="streak-progress-label">{streak} / {nextStreakReward.days} j</span>
            </div>
            <p className="text-[11px] text-white/80 italic">
              {daysLeft > 0
                ? `Continuez votre pratique quotidienne — plus que ${daysLeft} jour${daysLeft > 1 ? 's' : ''} avant votre prochaine récompense.`
                : 'Votre prochaine récompense est débloquée !'}
            </p>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="rewards-tabs">
        {[
          { id: 'streak', label: '🔥 Streak rewards' },
          { id: 'hours', label: '🎤 Heures de parole' },
          { id: 'levels', label: '🏆 Niveaux CEFR' },
          { id: 'achievements', label: '💎 Achievement Levels' },
          { id: 'history', label: '🎁 Historique' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${tab === t.id ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]' : 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37]'}`}
            data-testid={`rewards-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* STREAK tab */}
      {tab === 'streak' && (
        <div className="space-y-2" data-testid="streak-rewards-list">
          {STREAK_REWARDS.map((r) => {
            const unlocked = streak >= r.days;
            const isNext = !unlocked && nextStreakReward.days === r.days;
            return (
              <Card key={r.days} className={`px-5 py-4 flex items-start gap-4 ${isNext ? 'border-[#D4AF37]' : ''}`} testid={`streak-${r.days}`}>
                <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${unlocked ? 'bg-[#1A2F4A] text-[#D4AF37]' : isNext ? 'bg-[#D4AF37] text-[#1A2F4A]' : 'bg-[#FAF7F0] text-[#6B4E37] border border-[#E8E4DC]'}`}>
                  <div className="text-center">
                    <p className="text-base font-bold leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>{r.days}</p>
                    <p className="text-[8px] uppercase tracking-wider font-bold mt-0.5">jours</p>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <RewardKindBadge kind={r.kind} />
                    {unlocked && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">✓ Débloqué</span>}
                    {isNext && <span className="text-[10px] font-bold text-[#D4AF37] bg-[#FAF7F0] border border-[#D4AF37]/30 rounded-full px-2 py-0.5">Prochain</span>}
                  </div>
                  <p className="text-sm font-bold text-[#1A2F4A]">🎁 {r.reward}</p>
                  <p className="text-[11px] text-[#6B4E37] italic mt-0.5">{r.detail}</p>
                </div>
                {!unlocked && (
                  <Lock className="w-3.5 h-3.5 text-[#6B4E37] flex-shrink-0 mt-1" />
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* HOURS tab */}
      {tab === 'hours' && (
        <div className="space-y-3" data-testid="hours-rewards-block">
          <Card className="px-5 py-4" testid="hours-progress-card">
            <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-1">Temps de parole total</p>
            <p className="text-2xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>🎤 {minutesToHM(MOCK_REWARDS.speakingMinutes)}</p>
            <p className="text-[11px] text-[#6B4E37] italic mt-1">
              Encore {hoursLeft} h avant la prochaine récompense ({nextHoursReward.reward}).
            </p>
          </Card>
          <div className="space-y-2" data-testid="hours-rewards-list">
            {SPEAKING_HOURS_REWARDS.map((r) => {
              const unlocked = speakingHours >= r.hours;
              const isNext = !unlocked && nextHoursReward.hours === r.hours;
              return (
                <Card key={r.hours} className={`px-5 py-4 flex items-start gap-4 ${isNext ? 'border-[#D4AF37]' : ''}`} testid={`hours-${r.hours}`}>
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${unlocked ? 'bg-[#1A2F4A] text-[#D4AF37]' : isNext ? 'bg-[#D4AF37] text-[#1A2F4A]' : 'bg-[#FAF7F0] text-[#6B4E37] border border-[#E8E4DC]'}`}>
                    <div className="text-center">
                      <p className="text-base font-bold leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>{r.hours}</p>
                      <p className="text-[8px] uppercase tracking-wider font-bold mt-0.5">heures</p>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <RewardKindBadge kind={r.kind} />
                      {unlocked && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">✓ Débloqué</span>}
                      {isNext && <span className="text-[10px] font-bold text-[#D4AF37] bg-[#FAF7F0] border border-[#D4AF37]/30 rounded-full px-2 py-0.5">Prochain</span>}
                    </div>
                    <p className="text-sm font-bold text-[#1A2F4A]">🎁 {r.reward}</p>
                  </div>
                  {!unlocked && <Lock className="w-3.5 h-3.5 text-[#6B4E37] flex-shrink-0 mt-1" />}
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* LEVELS tab */}
      {tab === 'levels' && (
        <div className="space-y-2" data-testid="level-rewards-list">
          {LEVEL_REWARDS.map((r) => {
            const isDone = r.status === 'done';
            const isActive = r.status === 'active';
            const isLocked = r.status === 'locked';
            return (
              <Card key={r.level} className={`px-5 py-4 flex items-start gap-4 ${isActive ? 'border-[#D4AF37]' : ''}`} testid={`level-${r.level}`}>
                <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${isDone ? 'bg-[#1A2F4A] text-[#D4AF37]' : isActive ? 'bg-[#D4AF37] text-[#1A2F4A]' : 'bg-[#FAF7F0] text-[#6B4E37] border border-[#E8E4DC]'}`}>
                  <p className="text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{r.level}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {isDone && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">✓ Terminé</span>}
                    {isActive && <span className="text-[10px] font-bold text-[#D4AF37] bg-[#FAF7F0] border border-[#D4AF37]/30 rounded-full px-2 py-0.5">En cours</span>}
                    {isLocked && <span className="text-[10px] font-bold text-[#6B4E37] bg-white border border-[#E8E4DC] rounded-full px-2 py-0.5">Verrouillé</span>}
                  </div>
                  <p className="text-sm font-bold text-[#1A2F4A]">🏆 {r.certificate}</p>
                  <p className="text-[12px] text-[#1A2F4A]/90 mt-0.5">🎁 {r.bonus}</p>
                </div>
                {isLocked && <Lock className="w-3.5 h-3.5 text-[#6B4E37] flex-shrink-0 mt-1" />}
                {isDone && <CheckCircle2 className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-1" />}
              </Card>
            );
          })}
        </div>
      )}

      {/* ACHIEVEMENTS tab */}
      {tab === 'achievements' && (
        <div className="space-y-3" data-testid="achievements-block">
          {currentTier && (
            <Card className="px-5 py-4" testid="current-tier-card">
              <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-1">Niveau actuel</p>
              <p className="text-xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>💎 {currentTier.name}</p>
              <p className="text-[11px] text-[#6B4E37] italic mt-0.5">
                {nextTier ? `Continuez jusqu'à ${nextTier.threshold} jours pour atteindre ${nextTier.name}.` : 'Tu as atteint le niveau Platine — bravo !'}
              </p>
            </Card>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="achievement-levels-grid">
            {ACHIEVEMENT_LEVELS.map((a) => {
              const reached = streak >= a.threshold;
              return (
                <Card key={a.id} className={`px-5 py-4 ${reached ? 'border-[#D4AF37]' : ''}`} testid={`achievement-${a.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-base font-bold" style={{ fontFamily: "'Playfair Display', serif", color: a.color }}>💎 {a.name}</p>
                    {reached ? (
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">✓ Atteint</span>
                    ) : (
                      <span className="text-[10px] font-bold text-[#6B4E37] bg-white border border-[#E8E4DC] rounded-full px-2 py-0.5">À débloquer</span>
                    )}
                  </div>
                  <p className="text-[11px] text-[#6B4E37] mb-2">{a.threshold} jours de série consécutifs</p>
                  <ul className="space-y-1">
                    {a.benefits.map((b, i) => (
                      <li key={i} className="text-[12px] text-[#1A2F4A]/90 flex items-start gap-1.5">
                        <CheckCircle2 className="w-3 h-3 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* HISTORY tab */}
      {tab === 'history' && (
        <div className="space-y-2" data-testid="reward-history-list">
          {MOCK_REWARDS.history.map((h) => (
            <Card key={h.id} className="px-5 py-3 flex items-center gap-3" testid={`history-${h.id}`}>
              <Award className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <RewardKindBadge kind={h.kind} />
                  <p className="text-xs font-bold text-[#1A2F4A]">🎁 {h.reward}</p>
                </div>
                <p className="text-[10px] text-[#6B4E37] mt-0.5">Obtenu le {formatDate(h.at)}</p>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${
                h.status === 'utilisée'
                  ? 'bg-[#FAF7F0] text-[#6B4E37] border border-[#E8E4DC]'
                  : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              }`}>
                {h.status === 'utilisée' ? '✓ Utilisée' : '🎁 Disponible'}
              </span>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ===========================================================
   SECTION — BILLING (wired to real /api/orders + /api/subscriptions)
=========================================================== */
function BillingSection({ summary, onRefresh }) {
  const t = useT();
  const { format } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  useEffect(() => {
    setLoading(true);
    myOrders()
      .then((data) => { setOrders(data.items || []); setError(''); })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const activeSub = summary?.membership?.active_subscription;
  const latestOrder = summary?.membership?.latest_order;
  const planName = activeSub
    ? t({ fr: 'Abonnement actif', en: 'Active subscription' })
    : latestOrder?.product === 'membership'
      ? t({ fr: 'Adhésion (paiement unique)', en: 'Membership (one-time)' })
      : t({ fr: 'Aucun abonnement', en: 'No active plan' });

  const handleCancel = async () => {
    if (!activeSub?.razorpay_subscription_id) return;
    setCancelling(true);
    try {
      await cancelSubscription(activeSub.razorpay_subscription_id, true);
      setCancelConfirm(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="section-billing">
      <SectionTitle eyebrow={t({ fr: 'Abonnement & Facturation', en: 'Subscription & Billing' })}>
        {t({ fr: 'Plan, renouvellement, factures', en: 'Plan, renewal, invoices' })}
      </SectionTitle>

      <Card className="px-6 py-5" testid="current-plan">
        <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-1">
          {t({ fr: 'Plan actuel', en: 'Current plan' })}
        </p>
        <p className="text-xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>{planName}</p>
        {activeSub?.razorpay_subscription_id && (
          <p className="text-[11px] text-[#6B4E37] mt-1 font-mono">{activeSub.razorpay_subscription_id}</p>
        )}
        {activeSub?.plan_kind && (
          <p className="text-[11px] text-[#6B4E37] mt-1">
            {activeSub.plan_kind === 'club'
              ? t({ fr: 'Club Francophone · €5/mois', en: 'Club Francophone · €5/month' })
              : t({ fr: 'Adhésion Selora', en: 'Selora Membership' })}
          </p>
        )}
        <div className="flex flex-wrap gap-2 mt-4">
          <Link to="/pricing" className="bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-full px-4 py-2 text-xs font-bold min-h-[36px] inline-flex items-center" data-testid="upgrade-plan">
            {t({ fr: 'Changer de plan', en: 'Change plan' })}
          </Link>
          {activeSub?.razorpay_subscription_id && activeSub.status !== 'cancelled' && !cancelConfirm && (
            <button
              type="button"
              onClick={() => setCancelConfirm(true)}
              className="border border-[#E8E4DC] hover:border-red-300 text-[#1A2F4A] hover:text-red-700 rounded-full px-4 py-2 text-xs font-bold min-h-[36px] inline-flex items-center transition-colors"
              data-testid="cancel-subscription-btn">
              {t({ fr: 'Résilier mon abonnement', en: 'Cancel my subscription' })}
            </button>
          )}
        </div>

        {cancelConfirm && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="cancel-confirm-box">
            <p className="text-[13px] text-[#1A2F4A] mb-3">
              {t({
                fr: 'Confirmer la résiliation ? Votre abonnement restera actif jusqu\'à la fin du cycle en cours, puis ne se renouvellera plus.',
                en: 'Confirm cancellation? Your subscription will remain active until the end of the current cycle, then will not renew.',
              })}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={cancelling}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded-full px-4 py-2 text-xs font-bold min-h-[36px]"
                data-testid="cancel-confirm-yes">
                {cancelling
                  ? t({ fr: 'Résiliation...', en: 'Cancelling...' })
                  : t({ fr: 'Oui, résilier', en: 'Yes, cancel' })}
              </button>
              <button
                type="button"
                onClick={() => setCancelConfirm(false)}
                disabled={cancelling}
                className="border border-[#E8E4DC] text-[#1A2F4A] rounded-full px-4 py-2 text-xs font-bold min-h-[36px]"
                data-testid="cancel-confirm-no">
                {t({ fr: 'Non, garder', en: 'No, keep it' })}
              </button>
            </div>
          </div>
        )}
      </Card>

      <div>
        <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-2">
          {t({ fr: 'Historique de paiement', en: 'Payment history' })}
        </p>
        <Card className="overflow-hidden" testid="invoices">
          {loading && (
            <div className="px-4 py-6 text-center text-[12px] text-[#6B4E37]" data-testid="invoices-loading">
              {t({ fr: 'Chargement…', en: 'Loading…' })}
            </div>
          )}
          {!loading && orders.length === 0 && !error && (
            <div className="px-4 py-8 text-center text-[12px] text-[#6B4E37]" data-testid="invoices-empty">
              {t({ fr: "Aucun paiement pour l'instant.", en: 'No payments yet.' })}
              <div className="mt-3">
                <Link to="/pricing" className="text-[#D4AF37] hover:text-[#C9A961] text-[11px] font-bold uppercase tracking-widest">
                  {t({ fr: 'Découvrir les formules →', en: 'View plans →' })}
                </Link>
              </div>
            </div>
          )}
          {error && (
            <div className="px-4 py-4 text-center text-[12px] text-red-700 bg-red-50" data-testid="invoices-error">
              {error}
            </div>
          )}
          <div className="divide-y divide-[#F3F4F6]">
            {orders.map((o) => (
              <div key={o.id || o.razorpay_order_id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`invoice-${o.razorpay_order_id}`}>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-[#1A2F4A] truncate">
                    {o.label || o.product} · <span className="font-mono text-[10px] text-[#6B4E37]">{o.razorpay_order_id}</span>
                  </p>
                  <p className="text-[10px] text-[#6B4E37]">
                    {o.created_at ? new Date(o.created_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    {o.currency !== 'EUR' && (
                      <span className="text-[9px] italic ml-1">({format(o.amount_eur)} base)</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <p className="text-sm font-bold text-[#1A2F4A]">
                    {o.currency === 'INR' ? '₹' : o.currency === 'USD' ? '$' : o.currency === 'GBP' ? '£' : o.currency === 'CAD' ? 'C$' : o.currency === 'AUD' ? 'A$' : '€'}
                    {(o.amount_minor / 100).toLocaleString(undefined, {
                      minimumFractionDigits: o.currency === 'INR' ? 0 : 2,
                      maximumFractionDigits: o.currency === 'INR' ? 0 : 2,
                    })}
                  </p>
                  <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${
                    o.status === 'paid' ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
                      : 'text-amber-700 bg-amber-50 border border-amber-200'
                  }`}>
                    {o.status === 'paid'
                      ? t({ fr: 'Payé', en: 'Paid' })
                      : t({ fr: 'En attente', en: 'Pending' })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ===========================================================
   SECTION — NOTIFICATIONS
=========================================================== */
function NotificationsSection({ onChange }) {
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    myNotifications()
      .then((data) => setItems(data.items || []))
      .catch(() => {/* keep empty */})
      .finally(() => setLoading(false));
  }, []);

  const handleRead = async (id) => {
    setItems((arr) => arr.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try { await markNotificationRead(id); onChange && onChange(); } catch { /* ignore */ }
  };

  return (
    <div className="space-y-6" data-testid="section-notifications">
      <SectionTitle eyebrow={t({ fr: 'Notifications', en: 'Notifications' })}>
        {t({ fr: 'Vos alertes en temps réel', en: 'Your real-time alerts' })}
      </SectionTitle>
      <Card className="overflow-hidden" testid="notifications-list">
        {loading && (
          <div className="px-4 py-6 text-center text-[12px] text-[#6B4E37]">{t({ fr: 'Chargement…', en: 'Loading…' })}</div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-4 py-8 text-center text-[12px] text-[#6B4E37]" data-testid="notifications-empty">
            {t({ fr: 'Aucune notification pour le moment.', en: 'No notifications yet.' })}
          </div>
        )}
        <div className="divide-y divide-[#F3F4F6]">
          {items.map((n) => {
            const unread = !n.read;
            return (
              <button
                type="button"
                key={n.id}
                onClick={() => unread && handleRead(n.id)}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 ${unread ? 'bg-[#FAF7F0]/60' : ''}`}
                data-testid={`notification-${n.id}`}>
                <span className="text-lg flex-shrink-0" aria-hidden="true">{n.icon || '📣'}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] text-[#1A2F4A] leading-relaxed">{n.text}</p>
                  <p className="text-[10px] text-[#6B4E37] mt-0.5">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
                </div>
                {unread && <span className="w-2 h-2 rounded-full bg-[#D4AF37] flex-shrink-0 mt-2" />}
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

/* ===========================================================
   SECTION — SETTINGS
=========================================================== */
function SettingsSection({ user }) {
  return (
    <div className="space-y-6" data-testid="section-settings">
      <SectionTitle eyebrow="Paramètres">Profil, préférences et confidentialité</SectionTitle>

      <Card className="px-6 py-5" testid="profile-card">
        <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-3">Profil</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: 'Nom complet', value: user?.full_name || 'Apprenant(e)' },
            { label: 'Email', value: user?.email || 'student@selora.com' },
            { label: 'Niveau actuel', value: MOCK_PROFILE.level },
            { label: 'Membre depuis', value: formatDate(MOCK_PROFILE.joinedAt) },
          ].map((f) => (
            <div key={f.label}>
              <p className="text-[10px] tracking-[0.25em] font-bold text-[#6B4E37] uppercase mb-0.5">{f.label}</p>
              <p className="text-sm text-[#1A2F4A] font-medium">{f.value}</p>
            </div>
          ))}
        </div>
        <button className="mt-4 bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-full px-4 py-1.5 text-xs font-bold" data-testid="edit-profile">
          Modifier le profil
        </button>
      </Card>

      <Card className="px-6 py-5" testid="preferences-card">
        <p className="text-[10px] tracking-[0.25em] font-bold text-[#D4AF37] uppercase mb-3">Préférences</p>
        <div className="space-y-3">
          {[
            { label: 'Photo de profil', action: 'Téléverser', testid: 'upload-photo' },
            { label: 'Mot de passe', action: 'Modifier', testid: 'change-password' },
            { label: 'Notifications email', action: 'Configurer', testid: 'config-notifications' },
            { label: 'Langue de l\'interface', action: 'Français', testid: 'change-language' },
            { label: 'Confidentialité', action: 'Gérer', testid: 'privacy-settings' },
          ].map((p) => (
            <div key={p.label} className="flex items-center justify-between py-2 border-b border-[#F3F4F6] last:border-0">
              <p className="text-xs font-medium text-[#1A2F4A]">{p.label}</p>
              <button className="text-xs text-[#D4AF37] font-bold hover:text-[#1A2F4A]" data-testid={p.testid}>
                {p.action}
              </button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ===========================================================
   MAIN
=========================================================== */
export default function Dashboard() {
  const { user } = useAuth();
  const t = useT();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState('');
  const [active, setActive] = useState(() => {
    try {
      const saved = localStorage.getItem('selora_dashboard_section');
      if (saved && SIDEBAR_ITEMS.some((s) => s.id === saved)) return saved;
    } catch { /* ignore */ }
    return 'home';
  });

  useEffect(() => {
    localStorage.setItem('selora_dashboard_section', active);
  }, [active]);

  const refreshSummary = React.useCallback(() => {
    setSummaryLoading(true);
    getDashboardSummary()
      .then((data) => { setSummary(data); setSummaryError(''); })
      .catch((err) => setSummaryError(err.message))
      .finally(() => setSummaryLoading(false));
  }, []);

  useEffect(() => { refreshSummary(); }, [refreshSummary]);

  const unread = summary?.notifications_unread ?? 0;

  const renderSection = () => {
    switch (active) {
      case 'home': return <HomeSection user={user} setActive={setActive} summary={summary} loading={summaryLoading} />;
      case 'messages': return <MessagesSection />;
      case 'portfolio': return <PortfolioSection />;
      case 'progression': return <ProgressionSection summary={summary} />;
      case 'teacher-hours': return <TeacherHoursSection summary={summary} />;
      case 'feedback': return <FeedbackSection />;
      case 'club': return <ClubSection />;
      case 'rewards': return <RewardsSection summary={summary} />;
      case 'billing': return <BillingSection summary={summary} onRefresh={refreshSummary} />;
      case 'notifications': return <NotificationsSection onChange={refreshSummary} />;
      case 'settings': return <SettingsSection user={user} />;
      default: return <HomeSection user={user} setActive={setActive} summary={summary} loading={summaryLoading} />;
    }
  };

  return (
    <div className="bg-[#FAF7F0] min-h-screen" data-testid="dashboard-root">
      {summaryError && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-xs px-4 py-2 text-center" data-testid="dashboard-warning">
          {t({ fr: 'Impossible de charger les données en direct. Affichage du contenu de démonstration.', en: 'Unable to load live data. Showing demo content.' })}
        </div>
      )}
      <div className="flex">
        <Sidebar active={active} setActive={setActive} unread={unread} />
        <main className="flex-1 min-w-0">
          <MobileSectionSelect active={active} setActive={setActive} />
          <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-8 sm:py-10">
            {renderSection()}
          </div>
        </main>
      </div>
    </div>
  );
}
